import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './css/style.css'; // ← здесь стили со шрифтами
// Дизайн-система редактора — ПОСЛЕ старых стилей, и это важно: style.css
// достался от робототехники и задаёт светлый body через свои --color-*.
// Идя следом, cad-tokens.css перекрывает его при равной специфичности.
import './design/cad-tokens.css';


/*
 * SSO с Майнкрафтии.
 *
 * Редактор («Кубикон CAD») открывается из бокового меню школы отдельной
 * вкладкой на своём домене cad.minecraftia-school.ru. Чтобы ученик не входил
 * второй раз, школа даёт одноразовый тикет (TTL 30 сек) во фрагменте адреса:
 * `#ticket=...`. Здесь мы меняем его на JWT и кладём в localStorage ДО того,
 * как стартует React — иначе App успеет решить, что юзер не авторизован, и
 * покажет форму входа.
 *
 * Тикет, а не сам токен в ссылке: токен осел бы в истории браузера, в Referer
 * и в логах nginx. Фрагмент (#) вообще не уходит на сервер.
 *
 * Ключ localStorage — 'Authorization': именно его читают все сервисы в src/API
 * (см. API/API.js и соседние), формат тот же JWT v1 экосистемы.
 */
async function redeemSsoTicketIfPresent() {
    if (typeof window === 'undefined') return;

    try {
        let ticket = null;

        // Основной путь — hash (#ticket=xxx), query поддерживаем как запасной.
        const hash = (window.location.hash || '').replace(/^#/, '');
        if (hash) ticket = new URLSearchParams(hash).get('ticket');
        if (!ticket) ticket = new URLSearchParams(window.location.search).get('ticket');

        if (!ticket) return;

        // ВАЖНО: тикет главнее того, что лежит в хранилище.
        //
        // Раньше здесь стоял ранний выход «уже вошли — тикет не тратим», и это
        // ломало вход: в браузере оставался ПРОСРОЧЕННЫЙ токен от старого сайта
        // робототехники. Код видел его, тикет не гасил, AuthContext считал юзера
        // вошедшим — а первый же запрос отдавал 401, UserService делал logout(),
        // токен исчезал, и ProtectedRoute выкидывал обратно в школу.
        //
        // Раз школа прислала тикет, она только что подтвердила личность — им и
        // перезаписываем всё старое. Тикет одноразовый, но приходит на каждый
        // клик по «3D-редактору», так что тратить его не жалко.

        // user-сервис живёт на minecraftia-school.ru, а не на нашем домене:
        // на cad.* путь /api-user не проксируется. На localhost оставляем
        // относительный путь — там проксирует dev-сервер.
        const API_BASE = window.location.hostname === 'localhost'
            ? ''
            : 'https://minecraftia-school.ru';

        // Гасим тикет ТАМ ЖЕ, ГДЕ ОН ВЫДАН.
        //
        // Школа просит тикет у identity v2 (`/api/v2/identity/sso/play-ticket`),
        // и тот кладёт его в СВОЁ хранилище (`sso_ticket:` в Redis identity).
        // Старый user-сервис v1 про эту запись ничего не знает: у него свои
        // ключи `play_ticket:`. Поэтому попытка погасить v2-тикет через v1
        // всегда давала «недействителен» — это и был тот самый 401 в консоли.
        //
        // Порядок: сначала v2 (вся экосистема переехала на неё), v1 остаётся
        // запасным на случай, если тикет придёт из старого потока.
        const endpoints = [
            `${API_BASE}/api/v2/identity/sso/redeem-ticket`,
            `${API_BASE}/api-user/api/v1/auth/redeem-ticket`,
        ];

        for (const url of endpoints) {
            let resp;
            try {
                resp = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ticket }),
                });
            } catch (e) {
                continue;   // сеть/CORS — пробуем следующий
            }

            if (!resp.ok) continue;

            const data = await resp.json();
            // Токен приходит как "jwt" (v2 дублирует его и в "token") — на этом
            // уже спотыкались в Рублоксе: читали только data.token, получали
            // undefined, и SSO молча не работал.
            const jwt = data?.jwt || data?.token;
            if (jwt) {
                localStorage.setItem('Authorization', jwt);
                break;
            }
        }
    } catch (e) {
        // Тикет протух или сеть оборвалась — ниже сработает проверка срока
        // токена, и человек уедет в школу за новым.
    } finally {
        // Тикет в адресной строке не оставляем в любом случае.
        try {
            const url = new URL(window.location.href);
            url.hash = '';
            url.searchParams.delete('ticket');
            window.history.replaceState({}, '', url.toString());
        } catch (e) { /* старый браузер — не критично */ }
    }
}


/*
 * Выбросить протухший токен ДО старта React.
 *
 * В браузере мог остаться токен от старого сайта робототехники или просто
 * вчерашний (JWT живёт сутки). AuthContext смотрит только на факт наличия
 * ключа, поэтому считал бы такого юзера вошедшим — а первый запрос отдавал бы
 * 401, UserService делал logout(), и человека выкидывало «без причины».
 *
 * Проверяем срок локально, по полю exp: сервер для этого дёргать незачем.
 * Битый или просроченный — стираем, дальше ProtectedRoute отправит в школу,
 * та выдаст свежий тикет, и юзер вернётся сюда уже с рабочим токеном.
 */
function dropExpiredToken() {
    const raw = localStorage.getItem('Authorization');
    if (!raw) return;

    try {
        const token = raw.startsWith('Bearer ') ? raw.slice(7) : raw;
        const payload = JSON.parse(atob(token.split('.')[1]));
        // exp в секундах; небольшой запас, чтобы не начать работу с токеном,
        // который истечёт через мгновение.
        if (!payload.exp || payload.exp * 1000 <= Date.now() + 5000) {
            localStorage.removeItem('Authorization');
        }
    } catch (e) {
        // Не разобрали — значит это не наш JWT. Такому ключу тут не место.
        localStorage.removeItem('Authorization');
    }
}


const root = ReactDOM.createRoot(document.getElementById('root'));

// Порядок важен: сначала гасим тикет (он главнее старого содержимого
// хранилища), затем выбрасываем токен, если он всё-таки протух, и только потом
// рендерим — App при старте должен видеть либо рабочий JWT, либо пустоту.
redeemSsoTicketIfPresent().finally(() => {
    dropExpiredToken();
    root.render(
        <App/>
    );
});
